/**
 * A fixed number of slots written round and round: once full, each new item takes the
 * oldest one's place. Reads come out oldest first.
 */
export class RingBuffer<T> {
  private readonly slots: T[] = [];

  /** The slot the next item goes into once the buffer has wrapped: the oldest item's. */
  private next = 0;

  constructor(readonly capacity: number) {
    if (!Number.isInteger(capacity) || capacity < 1) {
      throw new RangeError(`A ring buffer needs a positive capacity, not ${capacity}`);
    }
  }

  get size(): number {
    return this.slots.length;
  }

  private get wrapped(): boolean {
    return this.slots.length === this.capacity;
  }

  private get newest(): number {
    return this.wrapped ? (this.next + this.capacity - 1) % this.capacity : this.slots.length - 1;
  }

  push(item: T): void {
    if (!this.wrapped) {
      this.slots.push(item);
      return;
    }
    this.slots[this.next] = item;
    this.next = (this.next + 1) % this.capacity;
  }

  /** The newest item; none on an empty buffer. */
  last(): T | undefined {
    return this.slots.length === 0 ? undefined : this.slots[this.newest];
  }

  /** Puts `item` in the newest item's place; nothing happens on an empty buffer. */
  replaceLast(item: T): void {
    if (this.slots.length > 0) {
      this.slots[this.newest] = item;
    }
  }

  /** Oldest first. */
  toArray(): T[] {
    return this.wrapped
      ? [...this.slots.slice(this.next), ...this.slots.slice(0, this.next)]
      : this.slots.slice();
  }

  clear(): void {
    this.slots.length = 0;
    this.next = 0;
  }
}
